import { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, AlertTriangle, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { PERMISSIONS } from '@pms/shared';
import { techSupportService } from '@/services/tech-support.service';
import type { TicketListItem } from '@/services/tech-support.service';
import { usePermission } from '@/hooks/usePermission';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { TechSupportDashboardCards } from './components/TechSupportDashboardCards';
import { TicketFilters } from './components/TicketFilters';
import { TicketCreateModal } from './components/TicketCreateModal';
import { TicketStatusBadge } from './components/TicketStatusBadge';
import { TicketPriorityBadge } from './components/TicketPriorityBadge';
import { TechSupportSettingsDialog } from './components/TechSupportSettingsDialog';

const COLUMNS: Column<TicketListItem>[] = [
  {
    key: 'ticket_number',
    header: '#',
    cell: (row) => <TicketNumberCell row={row} />,
    className: 'whitespace-nowrap w-28',
  },
  {
    key: 'title',
    header: 'Title',
    cell: (row) => (
      <div>
        <p className="font-medium text-foreground line-clamp-1">{row.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{row.created_by?.name}</p>
      </div>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    cell: (row) => <span className="text-sm text-muted-foreground">{row.category?.name ?? '—'}</span>,
    className: 'whitespace-nowrap',
  },
  {
    key: 'status',
    header: 'Status',
    cell: (row) => <TicketStatusBadge status={row.status} />,
  },
  {
    key: 'priority',
    header: 'Priority',
    cell: (row) => <TicketPriorityBadge priority={row.priority} />,
  },
  {
    key: 'assigned_to',
    header: 'Assigned To',
    cell: (row) => (
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {row.assigned_to?.name ?? <span className="italic">Unassigned</span>}
      </span>
    ),
  },
  {
    key: 'sla_due_at',
    header: 'SLA Due',
    cell: (row) =>
      row.sla_due_at ? (
        <span className={`flex items-center gap-1 text-sm whitespace-nowrap ${row.is_overdue ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
          {row.is_overdue && <AlertTriangle className="w-3.5 h-3.5" />}
          {format(new Date(row.sla_due_at), 'MMM d, h:mm a')}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    className: 'whitespace-nowrap',
  },
];

function TicketNumberCell({ row }: { row: TicketListItem }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/tech-support/${row.id}`)}
      className="font-mono text-sm text-primary font-semibold hover:underline"
    >
      {row.ticket_number}
    </button>
  );
}

export function TechSupportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { has } = usePermission();
  const canViewReports = has(PERMISSIONS.TECH_SUPPORT_VIEW_REPORTS);
  const canManageSettings =
    has(PERMISSIONS.TECH_SUPPORT_MANAGE_CATEGORIES) || has(PERMISSIONS.TECH_SUPPORT_MANAGE_SLA);

  const page = Number(searchParams.get('page') ?? '1');
  const filters = {
    q: searchParams.get('q') ?? undefined,
    status: searchParams.get('status') ?? undefined,
    priority: searchParams.get('priority') ?? undefined,
    category_id: searchParams.get('category_id') ?? undefined,
  };

  const setFilters = useCallback(
    (vals: { q?: string; status?: string; priority?: string; category_id?: string }) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      Object.entries(vals).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tech-support', 'tickets', { page, ...filters }],
    queryFn: () =>
      techSupportService.listTickets({
        page,
        limit: 20,
        q: filters.q,
        category_id: filters.category_id,
        status: filters.status as 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed' | undefined,
        priority: filters.priority as 'low' | 'medium' | 'high' | 'urgent' | undefined,
      }),
  });

  const tickets = data?.data?.data ?? [];
  const pagination = data?.data?.pagination;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Tech Support</h1>
          <p className="text-sm text-muted-foreground">Manage and track support tickets</p>
        </div>
        <div className="flex items-center gap-2">
          {canManageSettings && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSettingsOpen(true)}
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
          <PermissionGate permission={PERMISSIONS.TECH_SUPPORT_CREATE}>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </PermissionGate>
        </div>
      </div>

      {canViewReports && <TechSupportDashboardCards />}

      <TicketFilters
        values={{ q: filters.q, status: filters.status, priority: filters.priority, category_id: filters.category_id }}
        onChange={setFilters}
      />

      <DataTable
        columns={COLUMNS}
        rows={tickets}
        rowKey={(row) => row.id}
        loading={isLoading}
        error={isError}
        onRetry={() => refetch()}
        empty="No tickets found. Try adjusting your filters."
        pagination={
          pagination
            ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: (p) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('page', String(p));
                  setSearchParams(next);
                },
              }
            : undefined
        }
      />

      {createOpen && <TicketCreateModal onClose={() => setCreateOpen(false)} />}
      {settingsOpen && <TechSupportSettingsDialog onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
